/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	Fragment,
	type MouseEvent,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import { FadeIn } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';

import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { DotAnimationLoader } from '../../common/ui/ai-answer-loading/dot-animation-loader';
import { AISources } from '../../common/ui/ai-sources';
import { ErrorBoundary as CommonErrorBoundary } from '../../common/ui/error-boundary';
import { OpenChatButton } from '../../common/ui/open-chat/open-chat-button';
import { aiDefinitionsLoadUFOExperience } from '../../v2/common/ufo/experiences';
import { DefinitionSources } from '../../v2/definitions/body/sources/DefinitionSources';
import { LoadingDotWave } from '../../v2/definitions/loading/dot-wave/loading-dot-wave';
import { AIAnswerBody } from '../ai-answer-body/ai-answer-body';
import { useAIAnswerContext } from '../ai-answer-context';
import { AIAnswerFooter } from '../ai-answer-footer/ai-answer-footer';
import { AIAnswerTitle } from '../ai-answer-title';
import { onAIAnswerLoadingDialogShown, onAIAnswerSourceClick } from '../analytics';
import { ReadingAidsFollowUps } from '../reading-aids-follow-ups/reading-aids-follow-ups';
import { ReadingAidsContainer, ReadingAidsPopupContainer, ReadingAidsWrapper } from '../styled';
import type { NLPSearchResultSource } from '../types';

import { EditDefinition } from './edit-definition';
import { messages } from './messages';

const styles = cssMap({
	sourcesContainer: {
		paddingTop: '0',
		paddingRight: token('space.300'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.300'),
	},
	openChatContainer: {
		paddingBlockEnd: token('space.100'),
		paddingInline: token('space.300'),
	},
});

const MAX_SOURCES = 4;

export enum DefinitionAnswerState {
	LOADING = 'loading',
	ANSWERED = 'answered',
	ERRORED = 'errored',
}

const noop = () => {};

const OpenChat = ({ answerString, query }: { answerString: string; query: string }) => {
	const { formatMessage } = useIntl();

	if (!fg('kd_definitions_open_chat')) {
		return <Fragment />;
	}

	return (
		<Box xcss={styles.openChatContainer}>
			<OpenChatButton
				prompt={formatMessage(messages.definition_open_chat_user_initial_query, {
					// query only contains the highlighted word
					// we want to prefix the word "define" before
					// when a user's context is moved to chat
					term: query,
				})}
				query={query}
				answerString={answerString}
				source={'reading-aids'}
			/>
		</Box>
	);
};

export const ReadingAidsPopupContent: () => JSX.Element = () => {
	const {
		commonAttributes,
		callbacks,
		fireAnalyticsEvent,
		readingAidsOptions,
		isAnswered,
		showChatFollowUps,
	} = useAIAnswerContext();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

	const enableSmartLinkSources = readingAidsOptions?.enableSmartLinkSources;
	const { searchSessionId, sources, answerString, query } = commonAttributes;

	const { definitionCurationOptions } = readingAidsOptions || {};

	const editDefinitionEnabled = !!definitionCurationOptions;

	const { serveExperiment: serveDefinitionsUXExp } = getFrontendExperiment({
		abTestId: 'kd_definitions_ux_uplift_exp',
	});
	const serveDefinitionUXUpdates = serveDefinitionsUXExp && fg('kd_definitions_ux_uplift_exp_gate');

	const sourceClickCallback = useCallback(
		(index: number, source: NLPSearchResultSource, hostname?: string) => {
			fireAnalyticsEvent(
				onAIAnswerSourceClick({
					sourceIndex: index,
					hostname,
					sourceProduct: source.product || '',
					...commonAttributes,
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.SOURCE_CLICKED,
				commonAttributes,
				sourceProduct: source.product || '',
			});
			callbacks?.onAIAnswerSourceClick?.();
		},
		[fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent, callbacks],
	);

	return (
		<FadeIn>
			{(props) => (
				<ReadingAidsWrapper
					onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
					{...props}
					data-testId="definition-answer-card"
				>
					<AIAnswerTitle />
					<AIAnswerBody />
					{editDefinitionEnabled && !serveDefinitionUXUpdates && <EditDefinition />}
					{!serveDefinitionUXUpdates && (
						<Box xcss={styles.sourcesContainer}>
							<AISources
								sources={sources}
								searchSessionId={searchSessionId}
								onClickHandler={sourceClickCallback}
								limit={MAX_SOURCES}
								verticalLayout={true}
								enableSmartLinkSources={enableSmartLinkSources}
								isReadingAids={true}
							/>
						</Box>
					)}
					{serveDefinitionUXUpdates && <DefinitionSources sources={sources} />}
					{showChatFollowUps && (
						<Fragment>
							<CommonErrorBoundary>
								<ReadingAidsFollowUps />
							</CommonErrorBoundary>
							{isAnswered && !serveDefinitionUXUpdates && (
								<OpenChat answerString={answerString} query={query} />
							)}
						</Fragment>
					)}
					<AIAnswerFooter />
				</ReadingAidsWrapper>
			)}
		</FadeIn>
	);
};

export const DefinitionAnswerCard: () => JSX.Element = () => {
	const previousAnswerState = useRef<DefinitionAnswerState>();
	const { commonAttributes, loading, partialResponse, isAnswered, hasError, readingAidsOptions } =
		useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const [continueLoadingAnimation, setContinueLoadingAnimation] = useState(true);
	const { source } = commonAttributes;
	const isStreaming = !!partialResponse;
	const { onAnswerStateChange = noop, definitionLoadingConfig } = readingAidsOptions || {};

	useLayoutEffect(() => {
		if (!loading) {
			const countdownRemainingAnimation = setTimeout(() => {
				setContinueLoadingAnimation(false);
			}, 3000);

			return () => clearTimeout(countdownRemainingAnimation);
		}
	}, [loading]);

	useEffect(
		() => {
			// update answer state for external consumers
			// using a previous answer state to prevent multiple duplicate calls,
			// especially in the case of loading && streaming
			if (fg('kd_definitions_answer_state_signal')) {
				if (hasError && previousAnswerState.current !== DefinitionAnswerState.ERRORED) {
					onAnswerStateChange(DefinitionAnswerState.ERRORED);
					previousAnswerState.current = DefinitionAnswerState.ERRORED;
					aiDefinitionsLoadUFOExperience.failure();
				} else if (isAnswered && previousAnswerState.current !== DefinitionAnswerState.ANSWERED) {
					onAnswerStateChange(DefinitionAnswerState.ANSWERED);
					previousAnswerState.current = DefinitionAnswerState.ANSWERED;
					aiDefinitionsLoadUFOExperience.success();
				} else if (
					(loading || isStreaming) &&
					previousAnswerState.current !== DefinitionAnswerState.LOADING
				) {
					onAnswerStateChange(DefinitionAnswerState.LOADING);
					previousAnswerState.current = DefinitionAnswerState.LOADING;
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[loading, isStreaming, isAnswered, hasError],
	);

	const onLoadingAnimationShown = useCallback(() => {
		fireAnalyticsEvent(onAIAnswerLoadingDialogShown(commonAttributes, true));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fireAnalyticsEvent]);

	if (loading || isStreaming) {
		if (definitionLoadingConfig) {
			return <LoadingDotWave width={definitionLoadingConfig.width} />;
		}

		// will keep this old animation as a fallback in case definitionLoadingConfig is not passed in
		return (
			<DotAnimationLoader
				onAnimationShown={onLoadingAnimationShown}
				source={source}
				data-testid="ai-answer-animated-loader"
			/>
		);
	}

	return (
		<ReadingAidsPopupContainer continueAnimation={continueLoadingAnimation}>
			<ReadingAidsContainer>
				<ReadingAidsPopupContent />
			</ReadingAidsContainer>
		</ReadingAidsPopupContainer>
	);
};
